import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaWater, FaLeaf, FaCheck, FaClock, FaUsers, FaShieldAlt, FaLightbulb } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import hydroImage from '../../components/assets/Staff/Hydro.jpg';
import hydro1Image from '../../components/assets/Staff/Hydro1.jpeg';
import hydroVitality2Image from '../../components/assets/Staff/HydroVitality2.jpg';

const HydroVitality = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const sessionRef = useRef(null);
  const benefitsRef = useRef(null);
  const audienceRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'session': sessionRef,
      'benefits': benefitsRef,
      'audience': audienceRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'session', ref: sessionRef },
        { id: 'benefits', ref: benefitsRef },
        { id: 'audience', ref: audienceRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'session', label: 'How It Works', icon: FaWater },
    { id: 'benefits', label: 'What You May Notice', icon: FaLeaf },
    { id: 'audience', label: 'Who Is This For', icon: FaUsers }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Calm & Controlled",
      description: "A calm, controlled environment where the body can respond naturally"
    },
    {
      icon: FaWater,
      title: "Gentle Breathing Support",
      description: "Soft blend of hydrogen and oxygen for inhalation – no force or stimulation"
    },
    {
      icon: FaLeaf,
      title: "Not Medical",
      description: "Wellness experience that does not diagnose, treat, or cure any condition"
    }
  ];

  const whyPeopleCome = [
    "Persistent mental fatigue or brain fog that makes it harder to stay focused during busy weeks",
    "Uncomfortable breathing, especially during stress, long hours in dry or air-conditioned spaces, travel, or poor air quality",
    "Long workdays, physical exertion, or extended screen time when rest alone doesn't feel like enough",
    "Seasonal changes, frequent travel, or moments when the body feels overloaded rather than restored",
    "A subjective sense of heaviness around the head, nose, or sinus area",
    "Simply wanting breathing to feel easier and more natural during demanding days"
  ];

  const sessionProcess = [
    "You sit or lie back in a quiet, comfortable setting",
    "Gently inhale a soft blend of hydrogen and oxygen through a light nasal cannula",
    "The experience is silent, contact-free, and effortless",
    "There is nothing to control, no technique to learn, and no expectation to feel anything specific",
    "Optional: a small glass of hydrogen-rich water may complement the session"
  ];

  const sessionBenefits = [
    "Feeling calmer, more settled, and mentally clearer after sessions",
    "A sense of fresher, more comfortable breathing, particularly around the nose and sinus area",
    "Feeling lighter, less overstimulated, or more balanced during stressful or busy periods",
    "Over time, more in tune with the body's natural rhythms rather than constantly pushing through fatigue or overload"
  ];

  const targetAudience = [
    "Often feel mentally tired, foggy, or overstimulated even when you try to rest",
    "Notice that breathing doesn't always feel as comfortable or effortless as you would like during stress, long indoor days, or travel",
    "Feel drained after work, physical activity, or busy schedules and traditional relaxation doesn't fully reset you",
    "Spend a lot of time indoors in dry or air-conditioned environments or are exposed to poor air quality",
    "Are navigating periods of pressure, transition, or emotional load and want gentle, non-intrusive support that fits into real life"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="HydroVitality" description="HydroVitality therapy at Bioresonance Leicester – gentle breathing and vitality support. Calm, controlled sessions at 108 New Walk." path="/therapy/hydro-vitality" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">HydroVitality</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaWater className="mr-2" />
                Gentle breathing support for modern overload
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                HydroVitality
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                Modern life puts constant pressure on the body. Long hours indoors, stress, travel, poor air quality, screen time, and demanding schedules can leave you feeling mentally foggy, physically drained, or simply out of sync.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                HydroVitality is a non-invasive wellness experience designed for people who feel that everyday demands are starting to take their toll on their energy, breathing comfort, and overall sense of balance.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                This is not a medical service and it does not aim to diagnose, treat, or cure any condition. Instead, HydroVitality offers a calm, controlled environment where gentle breathing support allows the body to respond naturally, without force or stimulation.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>30-60 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container - same photo as HydroVitality card */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={hydroImage}
                      alt="HydroVitality therapy"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What Makes HydroVitality Special
              </h2>
              <p className="text-lg text-gray-600">
                A non-invasive wellness experience that offers space and allows the body to respond in its own way
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Awareness Disclaimer */}
            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaLightbulb className="text-xl text-gold" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    Not about fixing or pushing
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    HydroVitality is not about fixing the body or pushing it to perform. It is about supporting comfort, breathing ease, mental clarity, and balance in the context of everyday life.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={sessionRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                  How the session works
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    During a session, you sit or lie back in a quiet, comfortable setting while gently inhaling a soft blend of hydrogen and oxygen through a light nasal cannula. The experience is silent, contact-free, and effortless.
                  </p>
                  <p>
                    There is nothing to control, no technique to learn, and no expectation to feel anything specific. For those who wish, the session may be complemented with a small glass of hydrogen-rich water, offered as an optional part of the experience to support overall comfort.
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue/20 to-gold/20 h-80">
                    <img
                      src={hydro1Image}
                      alt="Session Process"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2 pointer-events-none"></div>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="text-2xl font-bold text-dark-grey mb-6 text-center">What to expect</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {sessionProcess.map((step, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{step}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="relative">
                  <div className="relative group">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gold/20 to-blue/20 h-96">
                      <img
                        src={hydroVitality2Image}
                        alt="Hydro Vitality device - Oxyhydrogen Therapy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You May Notice */}
      <section ref={benefitsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What you may notice
              </h2>
              <p className="text-lg text-gray-600">
                Experiences vary and no specific outcome is expected. That said, people commonly describe:
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="space-y-4 mb-6">
                  {sessionBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{benefit}</p>
                    </motion.div>
                  ))}
                </div>
                <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                  <p className="text-dark-grey font-semibold text-lg">
                    HydroVitality does not promise results. It offers space and allows the body to respond in its own way.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark-grey mb-4">Why people come</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  People don't usually come to HydroVitality just to relax. They come because something feels off.
                </p>
                <div className="space-y-3">
                  {whyPeopleCome.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-blue/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-blue text-xs" />
                      </div>
                      <p className="text-gray-600 text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section ref={audienceRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaUsers className="mr-2" />
                Gentle Support
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Who are these sessions for?
              </h2>
              <p className="text-lg text-gray-600">
                HydroVitality may be right for you if:
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-6">HydroVitality may be right for you if you:</h3>
                <div className="space-y-3">
                  {targetAudience.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-xs" />
                      </div>
                      <p className="text-gray-600 text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                  <h3 className="text-2xl font-bold text-dark-grey mb-4">HydroVitality may not be right for you if you:</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Are looking for a medical service, guaranteed results, or a high-intensity intervention. It is designed for people who value comfort, ease, and natural responses rather than stimulation or control.
                  </p>
                </div>
                <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-6 border border-gold/20">
                  <p className="text-dark-grey font-semibold">
                    HydroVitality is not about fixing the body or pushing it to perform. It is about supporting comfort, breathing ease, mental clarity, and balance in the context of everyday life.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Experience HydroVitality?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Gentle breathing support for when everyday demands take their toll – comfort, ease, and balance in the context of real life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default HydroVitality;
